import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { UserContext } from '../../Context/UserContext';
import CurrencySymbol from '../CurrencySymbol';
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft, FaTrash, FaCloudUploadAlt, FaImage, FaCalendarAlt, FaSave,
  FaCog, FaTimes, FaUsers, FaMapMarkerAlt, FaBox, FaChevronLeft, FaChevronRight, FaClock, FaEdit
} from "react-icons/fa";
import Cropper from 'react-easy-crop';
import { toast } from "react-toastify";
import "./CreateCustomerPage.css";
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { checkIsAdmin, checkIsGlobalAdmin } from "../../utils/authUtils";

const CustomDateTimePicker = ({ value, onChange, placeholder, type = 'datetime-local' }) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('days'); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date();
  });
  const [yearPage, setYearPage] = useState(() => Math.floor((currentDate.getFullYear()) / 12) * 12);

  const [popupDay, setPopupDay] = useState(() => value ? (new Date(value).getDate() || "") : "");
  const [popupMonth, setPopupMonth] = useState(() => value ? ((new Date(value).getMonth() + 1) || "") : "");
  const [popupYear, setPopupYear] = useState(() => value ? (new Date(value).getFullYear() || "") : "");
  
  const [popupTime, setPopupTime] = useState(() => {
    if (value && value.includes('T')) {
      return value.split('T')[1].substring(0, 5);
    }
    return "";
  });

  const [popupHour, setPopupHour] = useState(() => {
    if (value && value.includes('T')) {
      let h = parseInt(value.split('T')[1].split(':')[0], 10);
      return h % 12 || 12;
    }
    return 12;
  });
  const [popupMinute, setPopupMinute] = useState(() => {
    if (value && value.includes('T')) return parseInt(value.split('T')[1].split(':')[1], 10);
    return 0;
  });
  const [popupPeriod, setPopupPeriod] = useState(() => {
    if (value && value.includes('T')) return parseInt(value.split('T')[1].split(':')[0], 10) >= 12 ? 'PM' : 'AM';
    return 'AM';
  });

  // Normalize value: allow both '2026-09-05T00:00' and '2026-09-05 00:00'
  const normalizeValue = (v) => v ? String(v).replace(' ', 'T').substring(0, 16) : '';
  const normalizedValue = normalizeValue(value);

  const prevNormalizedRef = useRef('');

  useEffect(() => {
    const norm = normalizeValue(value);
    // Only sync if value actually changed to something meaningful
    if (norm === prevNormalizedRef.current) return;
    prevNormalizedRef.current = norm;

    if (norm) {
      const parsed = new Date(norm);
      if (!isNaN(parsed)) {
        setCurrentDate(parsed);
        setYearPage(Math.floor(parsed.getFullYear() / 12) * 12);
        setPopupDay(parsed.getDate() || "");
        setPopupMonth((parsed.getMonth() + 1) || "");
        setPopupYear(parsed.getFullYear() || "");
        if (norm.includes('T')) {
          const tPart = norm.split('T')[1];
          setPopupTime(tPart.substring(0, 5));
          let h = parseInt(tPart.split(':')[0], 10);
          setPopupHour(h % 12 || 12);
          setPopupMinute(parseInt(tPart.split(':')[1], 10));
          setPopupPeriod(h >= 12 ? 'PM' : 'AM');
        }
      }
    }
  }, [value]);

  const emitChange = (y, m, d, t) => {
    const formattedMonth = String(m).padStart(2, '0');
    const formattedDay = String(d).padStart(2, '0');
    if (type === 'datetime-local') {
      const timeStr = t || "00:00";
      onChange(`${y}-${formattedMonth}-${formattedDay}T${timeStr}`);
    } else {
      onChange(`${y}-${formattedMonth}-${formattedDay}`);
    }
  };

  const handleQuickChange = (inputType, val) => {
    if (inputType === 'day') setPopupDay(val);
    if (inputType === 'month') setPopupMonth(val);
    if (inputType === 'year') setPopupYear(val);

    const newDay = inputType === 'day' ? val : popupDay;
    const newMonth = inputType === 'month' ? val : popupMonth;
    const newYear = inputType === 'year' ? val : popupYear;

    const parsedYear = parseInt(newYear, 10);
    const parsedMonth = parseInt(newMonth, 10);
    const parsedDay = parseInt(newDay, 10);

    if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12 && !isNaN(parsedDay) && parsedDay >= 1 && parsedDay <= 31) {
      const newDateObj = new Date(parsedYear, parsedMonth - 1, parsedDay);
      if (!isNaN(newDateObj)) {
        setCurrentDate(newDateObj);
        setYearPage(Math.floor(parsedYear / 12) * 12);
        emitChange(parsedYear, parsedMonth, parsedDay, popupTime);
      }
    } else if (!isNaN(parsedYear) && parsedYear > 1000 && !isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
      setCurrentDate(new Date(parsedYear, parsedMonth - 1, 1));
      setYearPage(Math.floor(parsedYear / 12) * 12);
    }
  };

  const handleTimeSelect = (tType, val) => {
    let newH = popupHour, newM = popupMinute, newP = popupPeriod;
    if (tType === 'h') newH = val;
    if (tType === 'm') newM = val;
    if (tType === 'p') newP = val;
    setPopupHour(newH); setPopupMinute(newM); setPopupPeriod(newP);
    
    let h24 = newH;
    if (newP === 'PM' && newH !== 12) h24 += 12;
    if (newP === 'AM' && newH === 12) h24 = 0;
    
    const timeStr = `${String(h24).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
    setPopupTime(timeStr);
    
    const dYear = popupYear || selectedYear || year;
    const dMonth = popupMonth || (selectedMonth !== null ? selectedMonth + 1 : month + 1);
    const dDay = popupDay || selectedDay;
    if (dYear && dMonth && dDay) {
      emitChange(dYear, dMonth, dDay, timeStr);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const selectedYear2 = normalizedValue ? new Date(normalizedValue).getFullYear() : null;
  const selectedMonth2 = normalizedValue ? new Date(normalizedValue).getMonth() : null;
  const selectedDay2 = normalizedValue ? new Date(normalizedValue).getDate() : null;

  const displayValue = normalizedValue ? (type === 'datetime-local' ? normalizedValue.replace('T', ' ') : normalizedValue.substring(0, 10)) : "";

  return (
    <div className="custom-datepicker-container" ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div className="custom-datepicker-input" onClick={() => { setIsOpen(!isOpen); setView('days'); }} style={{ position: 'relative' }}>
        <input type="text" readOnly value={displayValue} placeholder={placeholder || (type === 'datetime-local' ? "YYYY-MM-DD HH:MM" : "YYYY-MM-DD")} style={{ cursor: 'pointer', background: '#F3F6FB', width: '100%', height: '44px', padding: '0 16px', borderRadius: '8px', border: '1px solid transparent', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box' }} />
        <FaCalendarAlt style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#604BE8', pointerEvents: 'none' }} />
      </div>

      {isOpen && (
        <>
          <div className="custom-datepicker-popup" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: type === 'datetime-local' ? '560px' : '320px', background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '24px', zIndex: 1001, border: '1px solid #e2e8f0', animation: 'slideDown 0.2s ease', boxSizing: 'border-box', display: 'flex', gap: '24px' }}>
            
            {/* Left Side: Date Calendar */}
            <div style={{ width: '100%', flex: 1 }}>
              <div className="popup-quick-inputs-container" style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '0 0 24px 0', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Quick Enter Date</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}><span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Day</span><input type="number" value={popupDay} onChange={(e) => handleQuickChange('day', e.target.value)} placeholder="DD" min="1" max="31" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} /></div>
                  <div style={{ flex: 1 }}><span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Month</span><input type="number" value={popupMonth} onChange={(e) => handleQuickChange('month', e.target.value)} placeholder="MM" min="1" max="12" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} /></div>
                  <div style={{ flex: 1.5 }}><span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Year</span><input type="number" value={popupYear} onChange={(e) => handleQuickChange('year', e.target.value)} placeholder="YYYY" min="1000" max="9999" style={{ width: '100%', height: '38px', padding: '0 12px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontWeight: '600', fontSize: '14px', color: '#1B1B29', boxSizing: 'border-box', textAlign: 'center' }} /></div>
                </div>
              </div>

              {view === 'days' && (
                <>
                  <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(year, month - 1, 1)); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronLeft /></button>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span onClick={(e) => { e.stopPropagation(); setView('months'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }}>{monthNames[month]}</span>
                      <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }}>{year}</span>
                    </div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(year, month + 1, 1)); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1B1B29', fontSize: '14px' }}><FaChevronRight /></button>
                  </div>
                  <div className="custom-datepicker-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '16px' }}>
                    {["Mo", "Tu", "We", "Th", "Fri", "Sa", "Su"].map((w, i) => <span key={i} style={{ fontSize: '13px', fontWeight: '700', color: '#1B1B29' }}>{w}</span>)}
                  </div>
                  <div className="custom-datepicker-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                    {Array.from({ length: startOffset }).map((_, i) => <div key={`empty-${i}`} />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const isSelected = selectedYear2 === year && selectedMonth2 === month && selectedDay2 === day;
                      return (
                        <button key={day} type="button" onClick={(e) => { e.stopPropagation(); emitChange(year, month + 1, day, popupTime); if(type !== 'datetime-local') setIsOpen(false); setView('days'); }} style={{ height: '36px', width: '100%', border: 'none', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: isSelected ? '700' : '600', cursor: 'pointer', background: isSelected ? '#1B65F2' : 'transparent', color: isSelected ? '#ffffff' : '#1B1B29' }}>{day}</button>
                      );
                    })}
                  </div>
                </>
              )}

              {view === 'months' && (
                <div>
                  <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setView('days'); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaChevronLeft /></button>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29' }}>Select Month</span>
                    <div style={{ width: '36px' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {monthNames.map((mName, index) => (
                      <button key={mName} type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(year, index, 1)); setView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: index === month ? '#1B65F2' : '#F3F6FB', color: index === month ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>{mName.substring(0, 3)}</button>
                    ))}
                  </div>
                </div>
              )}

              {view === 'years' && (
                <div>
                  <div className="custom-datepicker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 24px 0' }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage - 12); }} style={{ width: '36px', height: '36px', background: '#F3F6FB', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaChevronLeft /></button>
                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#1B1B29', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setView('days'); }}>{yearPage} - {yearPage + 11}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setYearPage(yearPage + 12); }} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FaChevronRight /></button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const y = yearPage + i;
                      return (
                        <button key={y} type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(new Date(y, month, 1)); setView('days'); }} style={{ height: '42px', border: 'none', borderRadius: '10px', background: y === year ? '#1B65F2' : '#F3F6FB', color: y === year ? '#ffffff' : '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>{y}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Side: Custom Time Picker */}
            {type === 'datetime-local' && (
              <div style={{ width: '220px', borderLeft: '1px solid #e2e8f0', paddingLeft: '24px', display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Select Time</span>
                <div style={{ display: 'flex', flex: 1, gap: '4px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                  {/* Hours */}
                  <div style={{ flex: 1, overflowY: 'auto', height: '240px', borderRight: '1px solid #e2e8f0' }}>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const h = i + 1;
                      const isSelected = popupHour === h;
                      return (
                        <div key={h} onClick={() => handleTimeSelect('h', h)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                          {String(h).padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                  {/* Minutes */}
                  <div style={{ flex: 1, overflowY: 'auto', height: '240px', borderRight: '1px solid #e2e8f0' }}>
                    {Array.from({ length: 60 }).map((_, i) => {
                      const m = i;
                      const isSelected = popupMinute === m;
                      return (
                        <div key={m} onClick={() => handleTimeSelect('m', m)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                          {String(m).padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                  {/* AM/PM */}
                  <div style={{ flex: 1, overflowY: 'auto', height: '240px' }}>
                    {['AM', 'PM'].map(p => {
                      const isSelected = popupPeriod === p;
                      return (
                        <div key={p} onClick={() => handleTimeSelect('p', p)} style={{ padding: '10px 0', textAlign: 'center', cursor: 'pointer', background: isSelected ? '#1B65F2' : '#fff', color: isSelected ? '#fff' : '#1B1B29', fontWeight: isSelected ? '700' : '500', fontSize: '14px' }}>
                          {p}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ background: '#F3F6FB', padding: '8px 12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', flex: 1, marginRight: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{String(popupHour).padStart(2, '0')}:{String(popupMinute).padStart(2, '0')} {popupPeriod}</span>
                    <FaClock style={{ color: '#64748b' }} />
                  </div>
                  <button type="button" onClick={() => setIsOpen(false)} style={{ padding: '8px 20px', background: '#604BE8', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>OK</button>
                </div>
              </div>
            )}
            
          </div>
        </>
      )}
    </div>
  );
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

const getCroppedImg = async (imageSrc, pixelCrop, targetWidth, targetHeight) => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return null
  }

  canvas.width = targetWidth || pixelCrop.width
  canvas.height = targetHeight || pixelCrop.height

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob((file) => {
      resolve(file)
    }, 'image/jpeg')
  })
}

const initialFormState = {
  description: "",
  total_price: 0,
  offer_price: "",
  offer_start_time: "",
  offer_end_time: "",
  enable_reward_points: false,
  reward_points: 0,
  items: [],
  images: [],
};

const ComboOffer = () => {
  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const [formData, setFormData] = useState(initialFormState);
  const [rawItems, setRawItems] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImageToCrop, setCurrentImageToCrop] = useState(null);
  const [cropTargetIndex, setCropTargetIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [targetSize, setTargetSize] = useState({ width: 768, height: 768 });
  const [aspect, setAspect] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [doctypeFields, setDoctypeFields] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyBranchesMap, setCompanyBranchesMap] = useState({});
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [currency, setCurrency] = useState("INR");

  const showCompanyAssign = (isGroupAdmin || isCompanyAdmin) && companyOptions.length > 1;

  const getCurrencySymbol = (currCode) => {
    const code = currCode?.toUpperCase();
    return <CurrencySymbol currencyCode={code} size={14} />;
  };

  const formatPrice = (price) => {
    const symbol = getCurrencySymbol(currency);
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>{symbol}{(Number(price) || 0).toFixed(2)}</span>;
  };
  const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';
  const showBranchAssign = (isGroupAdmin || isCompanyAdmin) && !isSpecificBranchActive && selectedCompanies.length > 0 && availableBranches.length > 0;

  const fetchDoctypeFields = async (currentBaseUrl = "") => {
    try {
      const response = await axios.get(`${currentBaseUrl || ""}/api/doctypes/Combo Offer`, { headers: getHeaders() });
      if (response.data && response.data.fields) setDoctypeFields(response.data.fields);
    } catch (error) { console.error(error); }
  };

  const getFieldHidden = (id) => doctypeFields.find(f => f.id === id)?.hidden === true;
  const getFieldLabel = (id, def) => doctypeFields.find(f => f.id === id)?.label || def;

  const getImageUrl = (path) => {
    if (!path) return "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E";
    if (path.startsWith('http')) return path;
    return `${baseUrl || ''}/api/combo-images/${path.split('/').pop()}`;
  };

  const toggleCompanySelection = (comp) => {
    setSelectedCompanies(prev => prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]);
  };

  const fetchCompanies = async (url, userObj) => {
    try {
      let comps = [];
      if (userObj.companies && userObj.companies.length > 0) {
        comps = userObj.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
      } else if (userObj.company_name || userObj.company) {
        comps = [userObj.company_name || userObj.company];
      } else {
        const res = await axios.get(`${url}/api/company-details`, { headers: getHeaders() });
        const details = res.data.companyDetails || [];
        comps = details.map(d => d.restaurantName).filter(n => n);
      }
      const finalComps = comps.length > 0 ? [...new Set(comps)] : ['POS 8'];
      setCompanyOptions(finalComps);

      const activeContext = localStorage.getItem('active_company');
      if (selectedCompanies.length === 0 && finalComps.length > 0) {
        if (activeContext && activeContext !== 'All' && finalComps.includes(activeContext)) {
          setSelectedCompanies([activeContext]);
        } else if (activeContext === 'All') {
          setSelectedCompanies(finalComps);
        } else if (!activeContext && finalComps.length === 1) {
          setSelectedCompanies([finalComps[0]]);
        }
      }

      if (finalComps.length > 0) {
        const branchPromises = finalComps.map(comp =>
          axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, { headers: { "X-Company-Name": comp, ...getHeaders() } })
            .then(res => ({ comp, data: res.data }))
        );
        const results = await Promise.all(branchPromises);
        const newMap = {};
        const allBranches = [];
        results.forEach(res => {
          const branches = res.data.map(b => typeof b === 'string' ? b : b.branch_name || b.branch || b.name || '').filter(b => b);
          newMap[res.comp] = branches;
          allBranches.push(...branches);
        });
        setCompanyBranchesMap(newMap);
        setAvailableBranches([...new Set(allBranches)]);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!configLoading) {
      fetchDoctypeFields(baseUrl);

      const fetchCurrency = async () => {
        try {
          const apiPath = baseUrl ? `${baseUrl}/api/settings` : '/api/settings';
          const response = await axios.get(apiPath, { headers: getHeaders() });
          const { currency: fetchedCurrency = "INR" } = response.data || {};
          setCurrency(fetchedCurrency.toUpperCase());
        } catch (error) {
          console.error("Error fetching currency settings", error);
          setCurrency("INR");
        }
      };
      fetchCurrency();

      const fetchComponents = async () => {
        try {
          const headers = getHeaders();
          const [itemsRes, combosRes] = await Promise.all([
            axios.get(`${baseUrl}/api/items`, { headers }),
            axios.get(`${baseUrl}/api/combo-offer`, { headers })
          ]);

          const items = itemsRes.data || [];
          const combos = combosRes.data || [];

          const now = new Date();
          const validCombos = combos.filter(c => {
            if (c.offer_end_time && new Date(c.offer_end_time) < now) return false;
            return true;
          });

          const extractedItems = [];

          const expandItem = (comp, type = 'Item') => {
            const variations = [];
            const companies = Array.isArray(comp.company_names) ? comp.company_names : (comp.company_name ? [comp.company_name] : []);
            const branches = Array.isArray(comp.branch_names) ? comp.branch_names : (comp.branch_name ? [comp.branch_name] : []);

            let basePrice = 0;
            if (type === 'Addon') basePrice = Number(comp.addon_price || comp.price || 0);
            else if (type === 'Combo') basePrice = Number(comp.offer_price || comp.total_price || comp.price || 0);
            else basePrice = Number(comp.price_list_rate || comp.price || 0);

            let possibleCompanies = companies.length > 0 ? companies : ['Global'];
            let possibleBranches = branches.length > 0 ? branches : ['Global'];

            possibleCompanies.forEach(cName => {
              if (String(cName).toLowerCase() !== 'global' && String(cName).toLowerCase() !== 'all companies') {
                let cPrice = basePrice;
                if (comp.company_prices && comp.company_prices[cName] !== undefined) {
                  cPrice = Number(comp.company_prices[cName]);
                }
                variations.push({
                  ...comp,
                  _id: `${comp._id}_${cName}`,
                  item_name: `${comp.name1 || comp.item_name || comp.description || 'Combo'} - ${cName}`,
                  price: cPrice,
                  company_names: [cName]
                });
              }

              possibleBranches.forEach(bName => {
                if (String(bName).toLowerCase() !== 'global' && String(bName).toLowerCase() !== 'all branches') {
                  let bPrice = basePrice;
                  if (comp.company_prices && comp.company_prices[cName] !== undefined) {
                    bPrice = Number(comp.company_prices[cName]);
                  }
                  if (comp.branch_prices && comp.branch_prices[bName] !== undefined) {
                    bPrice = Number(comp.branch_prices[bName]);
                  }

                  const branchKey = `${comp._id}_${bName}`;
                  if (!variations.some(v => v._id === branchKey)) {
                    variations.push({
                      ...comp,
                      _id: branchKey,
                      item_name: `${comp.name1 || comp.item_name || comp.description || 'Combo'} - ${bName}`,
                      price: bPrice,
                      branch_names: [bName]
                    });
                  }
                }
              });
            });

            if (variations.length === 0) {
              variations.push({
                ...comp,
                _id: comp._id,
                item_name: comp.name1 || comp.item_name || comp.description || 'Combo',
                price: basePrice
              });
            }

            return variations.map(v => ({ ...v, component_type: type }));
          };

          items.forEach(it => {
            const baseType = (it.type || 'Item').toLowerCase() === 'addon' ? 'Addon' : 'Item';
            extractedItems.push(...expandItem(it, baseType));

            if (it.addons && Array.isArray(it.addons)) {
              it.addons.forEach(addon => {
                if (addon.name1?.trim().toLowerCase() === it.item_name?.trim().toLowerCase()) return;
                const addonBase = {
                  ...addon,
                  _id: addon._id || `addon_${it._id}_${Math.random()}`,
                  company_names: addon.company_names || it.company_names,
                  branch_names: addon.branch_names || it.branch_names
                };
                extractedItems.push(...expandItem(addonBase, 'Addon'));
              });
            }

            if (it.combos && Array.isArray(it.combos)) {
              it.combos.forEach(combo => {
                if (combo.name1?.trim().toLowerCase() === it.item_name?.trim().toLowerCase()) return;
                const comboBase = {
                  ...combo,
                  _id: combo._id || `combo_${it._id}_${Math.random()}`,
                  company_names: combo.company_names || it.company_names,
                  branch_names: combo.branch_names || it.branch_names
                };
                extractedItems.push(...expandItem(comboBase, 'Combo'));
              });
            }
          });

          const combined = [
            ...extractedItems,
            ...validCombos.flatMap(c => expandItem(c, 'Combo'))
          ];

          setRawItems(combined);
        } catch (e) {
          console.error("Error fetching components:", e);
        }
      };
      fetchComponents();
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isGroupAdminRole = user.role === 'group_admin' || checkIsGlobalAdmin(user);
      const isCA = checkIsAdmin(user);
      const isBA = user.role?.toLowerCase().includes('branch') || user.role?.toLowerCase().includes('manager');

      setIsGroupAdmin(isGroupAdminRole);
      setIsCompanyAdmin(isCA || isGroupAdminRole);
      setIsBranchAdmin(isBA);

      if (isGroupAdminRole || isCA) {
        fetchCompanies(baseUrl, user);
      } else {
        const activeContext = localStorage.getItem('active_company') || '';
        setSelectedCompanies([activeContext || user.company_name || user.company]);
      }
      setCanWrite(true);
    }
  }, [configLoading, baseUrl]);

  const allItems = useMemo(() => {
    return rawItems.filter(item => {
      // Prevent circular reference: do not allow the current combo to be added to itself
      if (isEdit && formData) {
        const editId = formData._id;
        const globalId = formData.global_ref_id;
        if ((editId && item._id.startsWith(editId)) || (globalId && item._id.startsWith(globalId))) {
          return false;
        }
      }

      const itemCompanies = Array.isArray(item.company_names) ? item.company_names : (item.company_name ? [item.company_name] : []);
      const itemBranches = Array.isArray(item.branch_names) ? item.branch_names : (item.branch_name ? [item.branch_name] : []);

      const activeComp = localStorage.getItem('active_company') || '';
      const activeBranch = localStorage.getItem('active_branch') || '';

      const isAllComp = activeComp === '' || activeComp === 'All' || activeComp === 'All Companies';
      const isAllBranch = activeBranch === '' || activeBranch === 'All' || activeBranch === 'All Branches';

      const normalize = (str) => String(str || "").toLowerCase().replace(/[^a-z0-9]/gi, '');
      const matchTenancy = (a, b) => normalize(a) === normalize(b);

      const matchesCompany = isAllComp || itemCompanies.length === 0 || itemCompanies.some(c => matchTenancy(c, activeComp) || matchTenancy(c, 'Global') || matchTenancy(c, 'All Companies'));
      const matchesBranch = isAllBranch || itemBranches.length === 0 || itemBranches.some(b => matchTenancy(b, activeBranch) || matchTenancy(b, 'Global') || matchTenancy(b, 'All Branches'));

      return matchesCompany && matchesBranch;
    });
  }, [rawItems, isEdit, formData]);

  useEffect(() => {
    if (location.state?.combo) {
      const c = location.state.combo;
      setIsEdit(true);
      setFormData({ 
        ...initialFormState, 
        ...c,
        description: c.description || c.name || c.name1 || c.item_name || "",
        total_price: c.total_price || c.basePrice || c.price_list_rate || 0,
        offer_price: c.offer_price || "",
        offer_start_time: c.offer_start_time || "",
        offer_end_time: c.offer_end_time || "",
        enable_reward_points: c.enable_reward_points || false,
        reward_points: c.reward_points || 0
      });
      setSelectedComponents(c.items || c.comboItems || []);
      setPreviewUrls((c.images || []).map(img => getImageUrl(img)));
      if (c.company_names) setSelectedCompanies(c.company_names);
      else if (c.company_name) setSelectedCompanies([c.company_name]);

      if (c.branch_names) setSelectedBranches(c.branch_names);
      else if (c.branch_name) setSelectedBranches([c.branch_name]);
    }
  }, [location.state, baseUrl]);

  const handleSelection = (type, id) => {
    const it = allItems.find(i => i._id === id);
    if (!it) return;
    const price = it.price || 0;
    const comp = { type: "item", data: it, price: Number(price) };
    const newItems = [...selectedComponents, comp];
    setSelectedComponents(newItems);
    setFormData(prev => ({ ...prev, items: newItems, total_price: (prev.total_price || 0) + comp.price }));
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const removeSelection = (idx) => {
    const removed = selectedComponents[idx];
    const newItems = selectedComponents.filter((_, i) => i !== idx);
    setSelectedComponents(newItems);
    setFormData(prev => ({ ...prev, items: newItems, total_price: (prev.total_price || 0) - (removed?.price || 0) }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (let file of files) {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      try {
        const response = await axios.post(`${baseUrl}/api/upload-combo-image`, uploadFormData, {
          headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        setImages(prev => [...prev, response.data.filename]);
        setPreviewUrls(prev => [...prev, URL.createObjectURL(file)]);
      } catch (error) { toast.error("Upload failed"); }
    }
  };

  const handleEditImage = (index) => {
    setCurrentImageToCrop(previewUrls[index]);
    setCropTargetIndex(index);
    setCropModalOpen(true);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const handleCropSave = async () => {
    if (!currentImageToCrop || !croppedAreaPixels || cropTargetIndex === null) return;
    try {
      setLoading(true);
      const croppedImageBlob = await getCroppedImg(
        currentImageToCrop,
        croppedAreaPixels,
        targetSize.width,
        targetSize.height
      );
      const file = new File([croppedImageBlob], `cropped_image_${Date.now()}.jpeg`, { type: 'image/jpeg' });
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await axios.post(`${baseUrl}/api/upload-combo-image`, uploadFormData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });

      setImages(prev => {
        const newImages = [...prev];
        newImages[cropTargetIndex] = response.data.filename;
        return newImages;
      });
      setPreviewUrls(prev => {
        const newPreviews = [...prev];
        newPreviews[cropTargetIndex] = URL.createObjectURL(file);
        return newPreviews;
      });

      setCropModalOpen(false);
    } catch (error) {
      toast.error("Crop save failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleModalSave = async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);

      let finalBranches = selectedBranches;
      let finalCompanies = selectedCompanies;
      const activeBranchLoc = (localStorage.getItem('active_branch') || 'All Branches').trim();
      const activeCompLoc = (localStorage.getItem('active_company') || 'All Companies').trim();
      const isSpecBranch = activeBranchLoc.toLowerCase() !== 'all branches' && activeBranchLoc.toLowerCase() !== 'all' && activeBranchLoc !== '';
      const isSpecComp = activeCompLoc.toLowerCase() !== 'all companies' && activeCompLoc.toLowerCase() !== 'all' && activeCompLoc !== '';

      if (isSpecBranch && finalBranches.length === 0) finalBranches = [activeBranchLoc];
      if (isSpecComp && finalCompanies.length === 0) finalCompanies = [activeCompLoc];

      if (finalCompanies.length === 0 && (isGroupAdmin || isCompanyAdmin) && companyOptions.length > 0) {
        finalCompanies = ['All Companies'];
      }
      if (finalBranches.length === 0 && (isGroupAdmin || isCompanyAdmin) && availableBranches.length > 0) {
        finalBranches = ['All Branches'];
      }

      const payload = {
        ...formData,
        item_group: "Combos Offer",
        total_price: parseFloat(formData.total_price) || 0,
        offer_price: parseFloat(formData.offer_price) || 0,
        branch_name: finalBranches[0] || "",
        branch_names: finalBranches,
        company_name: finalCompanies[0] || "",
        company_names: finalCompanies,
        enable_reward_points: Boolean(formData.enable_reward_points),
        reward_points: parseFloat(formData.reward_points) || 0,
        images: images.length > 0 ? images : formData.images
      };
      if (isEdit) {
        if (payload.global_ref_id) {
          await axios.put(`${baseUrl}/api/combo-offer/bulk/${payload.global_ref_id}`, payload, { headers: getHeaders() });
        } else {
          await axios.put(`${baseUrl}/api/combo-offer/${payload._id}`, payload, { headers: getHeaders() });
        }
      } else {
        await axios.post(`${baseUrl}/api/combo-offer`, payload, { headers: getHeaders() });
      }
      toast.success("Saved!");
      navigate("/admin");
    } catch (err) { toast.error("Save failed"); }
    finally { setLoading(false); setShowAssignmentModal(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const activeContext = localStorage.getItem('active_company') || 'All';
    if (activeContext === 'All' && selectedCompanies.length === 0 && showCompanyAssign) {
      setShowAssignmentModal(true);
      return;
    }

    if (selectedBranches.length === 0 && showBranchAssign && !isEdit) {
      setShowAssignmentModal(true);
      return;
    }

    await handleModalSave(e);
  };

  return (
    <div className="create-customer-container main-customer-page">
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="header-back-btn" disabled={loading}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <div className="header-center">
          <h1>{isEdit ? "Edit Offer" : "New Combo Offer"}</h1>
        </div>

        <div className="header-right">
          <div className="header-actions">
            {showCompanyAssign && (
              <button
                type="button"
                className="customize-btn"
                onClick={() => setShowAssignmentModal(true)}
                style={{ background: '#eff6ff', color: '#3b82f6', border: '1.5px solid #dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '130px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)' }}
              >
                <FaUsers /> Company
              </button>
            )}

            {showBranchAssign && (
              <button
                type="button"
                className="customize-btn branch-btn"
                onClick={() => setShowAssignmentModal(true)}
                style={{ background: '#fff7ed', color: '#ea580c', border: '1.5px solid #ffedd5', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '130px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(234, 88, 12, 0.1)' }}
              >
                <FaMapMarkerAlt /> Branches
              </button>
            )}

            {isCompanyAdmin && (
              <button
                className="customize-btn"
                onClick={() => setShowCustomizeModal(true)}
                style={{ background: 'rgb(46, 204, 113)', color: '#ffffff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '130px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(46, 204, 113, 0.2)' }}
              >
                <FaCog /> Customize
              </button>
            )}
            <button
              className="submit-btn"
              onClick={handleSave}
              disabled={loading}
              style={{ backgroundColor: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '130px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', margin: 0, opacity: loading ? 0.7 : 1, transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2)' }}
            >
              <FaSave />
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
      <div className="header-divider"></div>

      <div className="customer-scroll-body">
        <div className="single-unified-card" style={{ background: '#ffffff', borderRadius: '30px', padding: '40px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', width: '100%', maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '30px' }}>
          
          {/* 1. Offer Name / Description */}
          {!getFieldHidden("description") && (
            <div className="form-group">
              <label>{getFieldLabel("description", "Offer Name / Description")}</label>
              <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter offer name" />
            </div>
          )}

          {/* 2. Components Selection */}
          {!getFieldHidden("items") && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="form-group" ref={dropdownRef} style={{ position: 'relative', zIndex: 10 }}>
                  <label>Add Components</label>
                  <input type="text" placeholder="Search items, addons, or combos..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setIsDropdownOpen(true); }} onFocus={() => setIsDropdownOpen(true)} />
                  {isDropdownOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', marginTop: '8px', maxHeight: '300px', overflowY: 'auto', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', zIndex: 100 }}>
                      {allItems.filter(i => (i.item_name || "").toLowerCase().includes(searchTerm.toLowerCase())).map(it => (
                        <div key={it._id} onClick={() => handleSelection('item', it._id)} style={{ padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{it.item_name}</span>
                            <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px', background: it.component_type === 'Combo' ? '#fef08a' : it.component_type === 'Addon' ? '#bfdbfe' : '#e2e8f0', color: it.component_type === 'Combo' ? '#854d0e' : it.component_type === 'Addon' ? '#1e3a8a' : '#475569', fontWeight: '800' }}>
                              {it.component_type}
                            </span>
                          </div>
                          <span style={{ fontWeight: '800', color: '#604be8', fontSize: '14px' }}>{formatPrice(it.price || 0)}</span>
                        </div>
                      ))}
                      {allItems.filter(i => (i.item_name || "").toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                        <div style={{ padding: '20px', color: '#94a3b8', fontSize: '14px', textAlign: 'center', fontWeight: '600' }}>No components match your selection. Try assigning a different company/branch.</div>
                      )}
                    </div>
                  )}
                </div>
                
                <div style={{ marginTop: '24px' }}>
                  {selectedComponents.map((comp, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: '#f8fafc', borderRadius: '16px', marginBottom: '12px', border: '1px solid #e2e8f0', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                      <img src={getImageUrl(comp.data.image || comp.data.addon_image || comp.data.combo_image)} alt="" style={{ width: '56px', height: '56px', borderRadius: '12px', objectFit: 'cover', background: '#fff', border: '1px solid #e2e8f0' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '800', fontSize: '15px', color: '#1e293b', marginBottom: '4px' }}>{comp.data.item_name || comp.data.name1}</div>
                        <div style={{ fontSize: '14px', color: '#604be8', fontWeight: '800' }}>{formatPrice(comp.price)}</div>
                      </div>
                      <button onClick={() => removeSelection(idx)} style={{ border: 'none', background: '#fee2e2', color: '#ef4444', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'} onMouseLeave={(e) => e.currentTarget.style.background = '#fee2e2'}>
                        <FaTrash size={14} />
                      </button>
                    </div>
                  ))}
                  {selectedComponents.length === 0 && (
                    <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #e2e8f0', borderRadius: '16px', color: '#94a3b8', fontWeight: '600', fontSize: '15px' }}>
                      No components selected yet.
                    </div>
                  )}
                </div>
              </div>
          )}

          {/* 3. Pricing */}
          <div className="form-grid">
            {!getFieldHidden("total_price") && (
              <div className="form-group">
                <label>{getFieldLabel("total_price", "Calculated Total Price")}</label>
                <input type="number" value={formData.total_price || 0} disabled style={{ backgroundColor: '#e2e8f0', cursor: 'not-allowed' }} />
              </div>
            )}
            {!getFieldHidden("offer_price") && (
              <div className="form-group">
                <label style={{ color: '#3b82f6' }}>{getFieldLabel("offer_price", "Offer Price")}</label>
                <input type="number" value={formData.offer_price || ""} onChange={(e) => setFormData({ ...formData, offer_price: e.target.value })} placeholder="0.00" style={{ border: '1.5px solid #3b82f6' }} />
              </div>
            )}
          </div>

          {/* 4. Schedule */}
          {(!getFieldHidden("offer_start_time") || !getFieldHidden("offer_end_time")) && (
              <div className="form-grid">
                {!getFieldHidden("offer_start_time") && (
                  <div className="form-group">
                    <label>{getFieldLabel("offer_start_time", "Start Time")}</label>
                    <CustomDateTimePicker value={formData.offer_start_time || ""} onChange={(val) => setFormData({ ...formData, offer_start_time: val })} type="datetime-local" />
                  </div>
                )}
                {!getFieldHidden("offer_end_time") && (
                  <div className="form-group">
                    <label>{getFieldLabel("offer_end_time", "End Time")}</label>
                    <CustomDateTimePicker value={formData.offer_end_time || ""} onChange={(val) => setFormData({ ...formData, offer_end_time: val })} type="datetime-local" />
                  </div>
                )}
              </div>
          )}

          {/* 5. Images */}
          {!getFieldHidden("images") && (
              <div>
                <div style={{ border: '2px dashed #cbd5e1', borderRadius: '15px', padding: '30px', textAlign: 'center', position: 'relative', background: '#f8fafc', transition: 'all 0.3s ease' }}>
                  <FaCloudUploadAlt size={40} color="#3b82f6" style={{ marginBottom: '10px' }} />
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>Upload New Images</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '5px' }}>Drag & drop or click to browse</div>
                  <input type="file" multiple onChange={handleImageUpload} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                </div>
                {previewUrls.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '15px', marginTop: '20px' }}>
                    {previewUrls.map((url, i) => (
                      <div key={i} style={{ position: 'relative', height: '100px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button type="button" onClick={() => handleEditImage(i)} style={{ position: 'absolute', top: 6, right: 36, background: '#3498db', border: 'none', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}><FaEdit size={10} /></button>
                        <button type="button" onClick={() => removeImage(i)} style={{ position: 'absolute', top: 6, right: 6, background: '#ef4444', border: 'none', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}><FaTrash size={10} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          )}

          {/* 5. Reward Points */}
          <div className="form-section-card theme-blue" style={{ marginTop: '24px', padding: '24px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px' }}>
            <div className="section-header" style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>Reward Points</h3>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginBottom: formData.enable_reward_points ? '16px' : '0' }}>
              <div className="form-check form-switch" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.enable_reward_points}
                  onChange={(e) => setFormData({ ...formData, enable_reward_points: e.target.checked })}
                  style={{ width: '40px', height: '20px', cursor: 'pointer', margin: 0 }}
                />
                <label style={{ margin: 0, fontWeight: '700', color: '#475569' }}>Enable Reward Points</label>
              </div>
            </div>
            {formData.enable_reward_points && (
              <div className="form-group">
                <label style={{ color: '#3b82f6', fontWeight: '600' }}>Reward Points Earned</label>
                <input 
                  type="number" 
                  value={formData.reward_points || ""} 
                  onChange={(e) => setFormData({ ...formData, reward_points: e.target.value })} 
                  placeholder="e.g. 10" 
                  min="0"
                  step="0.1"
                  style={{ border: '1.5px solid #3b82f6', width: '100%', padding: '10px', borderRadius: '8px' }} 
                />
              </div>
            )}
          </div>

          {/* Dynamic Fields */}
            {doctypeFields.filter(f => !['description', 'items', 'total_price', 'offer_price', 'offer_start_time', 'offer_end_time', 'images'].includes(f.id) && !f.hidden).length > 0 && (
                <div className="form-grid">
                  {doctypeFields.filter(f => !['description', 'items', 'total_price', 'offer_price', 'offer_start_time', 'offer_end_time', 'images'].includes(f.id) && !f.hidden).map(field => (
                    <div key={field.id} className="form-group">
                      <label>{field.label}</label>
                      {field.type === 'Date' ? (
                        <CustomDateTimePicker value={formData[field.id] || ""} onChange={(val) => setFormData({ ...formData, [field.id]: val })} type="date" />
                      ) : field.type === 'Time' ? (
                        <input type="time" value={formData[field.id] || ""} onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })} />
                      ) : field.type === 'Datetime' ? (
                        <CustomDateTimePicker value={formData[field.id] || ""} onChange={(val) => setFormData({ ...formData, [field.id]: val })} type="datetime-local" />
                      ) : (
                        <input type="text" value={formData[field.id] || ""} onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })} />
                      )}
                    </div>
                  ))}
                </div>
            )}

        </div>
      </div>

      {showAssignmentModal && (
        <div className="add-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="add-modal" style={{ background: '#fff', padding: '30px', borderRadius: '24px', width: '100%', maxWidth: '600px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '20px', fontWeight: '800' }}>Company & Branch Assignments</span>
              <button onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              {showCompanyAssign && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#3b82f6', marginBottom: '10px' }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companyOptions.map((comp, idx) => (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff6ff', borderRadius: '12px', border: '1.5px solid #dbeafe', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => { setSelectedCompanies(prev => e.target.checked ? [...prev, comp] : prev.filter(c => c !== comp)); }} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {showBranchAssign && (
                <div className="assignment-card">
                  <h4 style={{ color: '#10b981', marginBottom: '10px' }}><FaMapMarkerAlt /> Select Branch</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {availableBranches.map((branch, idx) => (
                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input type="checkbox" checked={selectedBranches.includes(branch)} onChange={(e) => { setSelectedBranches(prev => e.target.checked ? [...prev, branch] : prev.filter(b => b !== branch)); }} style={{ width: '18px', height: '18px', accentColor: '#10b981' }} />
                        {branch}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
              <button onClick={() => setShowAssignmentModal(false)} style={{ padding: '12px 24px', borderRadius: '12px', border: 'none', background: '#f1f5f9', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleModalSave} disabled={loading} style={{ padding: '12px 24px', borderRadius: '12px', border: 'none', background: '#3b82f6', color: '#fff', fontWeight: '600', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? "Saving..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => fetchDoctypeFields(baseUrl)}
        targetDocType="Combo Offer"
      />

      {cropModalOpen && (
        <div className="add-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="add-modal" style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '600px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
              <h5 className="modal-title" style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Crop Image</h5>
              <button className="close-button" onClick={() => setCropModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', fontWeight: 'bold' }}>X</button>
            </div>
            <div className="crop-container-wrapper" style={{ height: '400px', position: 'relative', width: '100%', background: '#333' }}>
              <Cropper
                image={currentImageToCrop}
                crop={crop}
                zoom={zoom}
                minZoom={0.1}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(croppedArea, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                restrictPosition={false}
              />
            </div>
            <div className="controls" style={{ padding: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '15px', justifyContent: 'center', background: '#f8f9fa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label style={{ fontSize: '14px' }}>Zoom:</label>
                <input
                  type="range"
                  value={zoom}
                  min={0.1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(e.target.value)}
                  style={{ width: '100px', cursor: 'pointer' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label style={{ fontSize: '14px' }}>Width (px):</label>
                <input
                  type="number"
                  value={targetSize.width}
                  onChange={(e) => {
                    const w = Number(e.target.value);
                    setTargetSize(prev => ({ ...prev, width: w }));
                    setAspect(w / targetSize.height);
                  }}
                  style={{ width: '80px', padding: '5px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label style={{ fontSize: '14px' }}>Height (px):</label>
                <input
                  type="number"
                  value={targetSize.height}
                  onChange={(e) => {
                    const h = Number(e.target.value);
                    setTargetSize(prev => ({ ...prev, height: h }));
                    setAspect(targetSize.width / h);
                  }}
                  style={{ width: '80px', padding: '5px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <button type="button" onClick={handleCropSave} disabled={loading} style={{ padding: '8px 20px', background: '#604BE8', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '600' }}>
                {loading ? "Saving..." : "Save Crop"}
              </button>
            </div>
            {croppedAreaPixels && (
              <div style={{ textAlign: 'center', paddingBottom: '10px', fontSize: '12px', color: '#666', background: '#f8f9fa' }}>
                Selection Size: {Math.round(croppedAreaPixels.width)} x {Math.round(croppedAreaPixels.height)} px
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboOffer;
